import type { ReactNode } from 'react';
import { useController } from 'react-hook-form';

import { FormField, SelectInput, type FormFieldProps } from '@/modules/forms';
import { useDefaultAccessRole } from '@/modules/settings/hooks';
import { AccessRole, type EntityWithPermissions } from '@/types';
import { permissionByRole } from '@/utilities/permission';

interface EntityWithId {
    id: string;
}

interface Option<Value extends EntityWithId> {
    value: Value['id'];
    entity: Value;
    label: string;
    key: string;
}

interface RenderValueFn<Value extends EntityWithPermissions> {
    (
        value: Value[],
        actions: {
            onUpdate: (value: Value) => void;
            onRemove: (value: Value['id']) => void;
        },
    ): ReactNode;
}
export interface SelectFieldProps<Value extends EntityWithPermissions> {
    id: string;
    name: string;
    placeholder: string;
    label: FormFieldProps['label'];
    children: RenderValueFn<Value>;
    loading?: boolean;
    loadingMore: boolean;
    onLoadMore: () => void;
    hasMore: boolean;
    onSearch: (filter: string) => void;
    onChange?: () => void;
    options: Option<EntityWithId>[];
    popupShouldIgnoreParentContainer?: boolean;
    /**
     * Role a newly picked entity starts at. Overrides the organisation's default sharing role, which
     * can be autofill-only or passkey-only — states a vault member cannot represent.
     */
    defaultRole?: AccessRole;
}

export function SelectField<Value extends EntityWithPermissions>({
    id,
    placeholder,
    name,
    children,
    label,
    options,
    onChange,
    loading,
    loadingMore,
    onLoadMore,
    onSearch,
    hasMore,
    popupShouldIgnoreParentContainer,
    defaultRole,
}: SelectFieldProps<Value>) {
    const dummyValue = [] satisfies Value[];

    const defaultAccessRole = useDefaultAccessRole();

    const { field } = useController({ name });

    const value = field.value ? (field.value as Value[]) : dummyValue;

    const handleChange = (val: Value[]) => {
        field.onChange(val);
        onChange?.();
    };

    return (
        <FormField name={name} label={label}>
            <SelectInput
                loading={loading}
                filterOption={false}
                id={id}
                value={dummyValue}
                placeholder={placeholder}
                onBlur={field.onBlur}
                onChange={(val: string) => {
                    const matchingOption = options.find(option => val === option.value);

                    if (!matchingOption) {
                        return;
                    }

                    handleChange([
                        ...value,
                        {
                            ...matchingOption.entity,
                            accessPermissions: permissionByRole[defaultRole ?? defaultAccessRole ?? AccessRole.VIEWER],
                        } as Value,
                    ]);

                    onSearch('');
                }}
                options={options
                    .filter(option => !value.find(v => v.id === option.value))
                    .map(({ value, label, key }) => ({ value, label, key }))}
                showSearch
                onSearch={onSearch}
                optionFilterProp='children'
                loadingMore={loadingMore}
                loadMore={onLoadMore}
                hasMore={hasMore}
                popupShouldIgnoreParentContainer={popupShouldIgnoreParentContainer}
            />
            {children(value, {
                onUpdate: (val: Value) => {
                    handleChange(value.map(v => (v.id === val.id ? val : v)));
                },
                onRemove: (id: Value['id']) => {
                    handleChange(value.filter(v => v.id !== id));
                },
            })}
        </FormField>
    );
}
